import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bookingService } from '../services/api';
import axios from 'axios';
import { FaChevronLeft, FaChevronRight, FaCalendarDay } from 'react-icons/fa';
import '../styles/Calendar.css';


// Feierpaket-Status, die im Kalender erscheinen (alle anderen, z. B. Neu, Angefragt, Archiviert, werden ausgeblendet)
const FEIERPAKET_IM_KALENDER = ['Bestätigt', 'Bezahlt', 'Versendet', 'Zurück', 'Abgeschlossen'];

function Calendar() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month'); // month or week

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      // Alle Buchungen laden - Feierpakete, DJ UND Livemusik
      const [feierpaketResponse, djResponse, livemusikResponse] = await Promise.all([
        bookingService.getAll({ limit: 1000 }),
        axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/dj`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/livemusik`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      const feierpakete = feierpaketResponse.bookings || [];
      const djBookings = djResponse.data.bookings || [];
      const livemusikBookings = livemusikResponse.data.bookings || [];
      
      console.log('📦 Feierpakete geladen:', feierpakete.length);
      console.log('🎧 DJ-Buchungen geladen:', djBookings.length);
      console.log('🎤 Livemusik-Buchungen geladen:', livemusikBookings.length);
      
      // Alle zusammenführen mit Typ-Kennzeichnung
      const allBookings = [
        ...feierpakete.map(b => ({ ...b, bookingType: 'feierpaket' })),
        ...djBookings.map(b => ({ ...b, bookingType: 'dj' })),
        ...livemusikBookings.map(b => ({ ...b, bookingType: 'livemusik' }))
      ];
      
      console.log('📅 Gesamt im Kalender:', allBookings.length);
      
      setBookings(allBookings);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Kalender-Daten berechnen
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const firstDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  // Vorherige Monat Tage
  const prevMonthDays = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
  const prevMonthLastDay = new Date(year, month, 0).getDate();

  // Navigation
  const goToPrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Buchungen für einen Tag holen
  const getBookingsForDate = (date) => {
    // Lokales Datum als String (YYYY-MM-DD)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return bookings.filter(booking => {
      // DJ-Buchungen: Event-Datum (mit Bindestrich)
      if (booking.bookingType === 'dj' && booking['Event-Datum']) {
        const eventDateStr = booking['Event-Datum'].split('T')[0];
        if (eventDateStr === dateStr) return true;
      }
      
      // Livemusik-Buchungen: Event-Datum (mit Bindestrich)
      if (booking.bookingType === 'livemusik' && booking['Event-Datum']) {
        const eventDateStr = booking['Event-Datum'].split('T')[0];
        if (eventDateStr === dateStr) return true;
      }
      
      // Feierpakete: NUR Eventdatum – und nur, wenn wirklich gebucht (nicht Neu/Angefragt/Archiviert)
      if (booking.bookingType === 'feierpaket' && booking.Eventdatum && FEIERPAKET_IM_KALENDER.includes(booking.Status)) {
        const eventDateStr = booking.Eventdatum.split('T')[0];
        if (eventDateStr === dateStr) return true;
      }
      
      return false;
    });
  };

  // Typ und Farbe für Buchung
  const getBookingType = (booking) => {
    if (booking.bookingType === 'dj') {
      return {
        type: 'DJ Buchung',
        color: '#8b5cf6',
        icon: '🎧'
      };
    }
    
    if (booking.bookingType === 'livemusik') {
      return {
        type: 'Livemusik',
        color: '#10b981',
        icon: '🎤'
      };
    }
    
    // Feierpaket
    return {
      type: 'Feierpaket',
      color: '#f40903',
      icon: '📦'
    };
  };

  const handleBookingClick = (booking) => {
    if (booking.bookingType === 'dj') {
      navigate(`/dj/${booking.id}`);
    } else if (booking.bookingType === 'livemusik') {
      navigate(`/livemusik/${booking.id}`);
    } else {
      navigate(`/feierpakete/${booking.id}`);
    }
  };

  // Kalender-Tage generieren
  const calendarDays = [];
  
  // Vorheriger Monat
  for (let i = prevMonthDays; i > 0; i--) {
    const date = new Date(year, month - 1, prevMonthLastDay - i + 1);
    calendarDays.push({
      date,
      isCurrentMonth: false,
      bookings: getBookingsForDate(date)
    });
  }
  
  // Aktueller Monat
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    calendarDays.push({
      date,
      isCurrentMonth: true,
      bookings: getBookingsForDate(date)
    });
  }
  
  // Nächster Monat (auffüllen bis 42 Tage = 6 Wochen)
  const remainingDays = 42 - calendarDays.length;
  for (let day = 1; day <= remainingDays; day++) {
    const date = new Date(year, month + 1, day);
    calendarDays.push({
      date,
      isCurrentMonth: false,
      bookings: getBookingsForDate(date)
    });
  }

  // Ist heute?
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="calendar-page">
      <div className="calendar-header">
        <div>
          <h1>Kalender</h1>
          <p className="text-gray">Alle Events und Buchungen im Überblick</p>
        </div>
        <button onClick={goToToday} className="btn btn-secondary">
          <FaCalendarDay /> Heute
        </button>
      </div>

      {/* Navigation */}
      <div className="calendar-nav card">
        <button onClick={goToPrevMonth} className="nav-btn">
          <FaChevronLeft />
        </button>
        
        <h2 className="calendar-title">
          {currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
        </h2>
        
        <button onClick={goToNextMonth} className="nav-btn">
          <FaChevronRight />
        </button>
      </div>

      {/* Legende */}
      <div className="calendar-legend card">
        <div className="legend-item">
          <span className="legend-color" style={{ background: '#f40903' }}></span>
          <span>Feierpaket 📦</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ background: '#8b5cf6' }}></span>
          <span>DJ Buchung 🎧</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ background: '#10b981' }}></span>
          <span>Livemusik 🎤</span>
        </div>
      </div>

      {/* Kalender Grid */}
      <div className="calendar-grid card">
        {/* Wochentage */}
        <div className="calendar-weekdays">
          <div className="weekday">Mo</div>
          <div className="weekday">Di</div>
          <div className="weekday">Mi</div>
          <div className="weekday">Do</div>
          <div className="weekday">Fr</div>
          <div className="weekday">Sa</div>
          <div className="weekday">So</div>
        </div>

        {/* Tage */}
        <div className="calendar-days">
          {calendarDays.map((day, index) => (
            <div
              key={index}
              className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${isToday(day.date) ? 'today' : ''} ${day.bookings.length > 0 ? 'has-bookings' : ''}`}
            >
              <div className="day-number">{day.date.getDate()}</div>
              
              {day.bookings.length > 0 && (
                <div className="day-bookings">
                  {day.bookings.slice(0, 3).map((booking, i) => {
                    const type = getBookingType(booking);
                    const name = (booking.bookingType === 'dj' || booking.bookingType === 'livemusik')
                      ? `${booking.Vorname || ''} ${booking.Nachname || ''}`.trim()
                      : `${booking.Vorname || ''} ${booking.Nachname || ''}`.trim();
                    const location = (booking.bookingType === 'dj' || booking.bookingType === 'livemusik')
                      ? booking['Event Ort']
                      : booking.Ort;
                    
                    return (
                      <div
                        key={i}
                        className="booking-dot"
                        style={{ background: type.color }}
                        onClick={() => handleBookingClick(booking)}
                        title={`${type.icon} ${name} - ${location || 'Kein Ort'}`}
                      >
                        <span className="booking-dot-text">
                          {type.icon} {name}
                        </span>
                      </div>
                    );
                  })}
                  {day.bookings.length > 3 && (
                    <div className="booking-more">
                      +{day.bookings.length - 3} weitere
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="calendar-stats">
        <div className="stat-box">
          <h3>{bookings.length}</h3>
          <p>Buchungen gesamt</p>
        </div>
        <div className="stat-box">
          <h3>{bookings.filter(b => {
            const eventDate = b.Eventdatum ? new Date(b.Eventdatum) : null;
            return eventDate && eventDate.getMonth() === month && eventDate.getFullYear() === year;
          }).length}</h3>
          <p>Events in diesem Monat</p>
        </div>
        <div className="stat-box">
          <h3>{bookings.filter(b => b.Status === 'Bestätigt' || b.Status === 'Versendet').length}</h3>
          <p>Aktive Buchungen</p>
        </div>
      </div>
    </div>
  );
}

export default Calendar;
